// TaskDetailDialog.js
import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Textarea } from '../../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Badge } from '../../ui/badge';
import { AlertCircle, Calendar, User, Link2, MessageSquare, FileText } from 'lucide-react';
import { useTaskDetail } from './hooks/useTaskDetail';
import { CommunicationDialog } from './CommunicationDialog';
import ActivityDialogFooter from './shared/ActivityDialogFooter';

export const TaskDetailDialog = ({ open, onOpenChange, task, onSuccess, associations = [], users = [], usersMap = {}, communications = [], contactEntries = [], horses = [], accounts = [], showAccountDropdown = false, onTaskUpdate, onOpenCommDetail, canEditAccount, horseNotes = [], onOpenNoteDetail }) => {
  const {
    isEditing, setIsEditing,
    saving,
    showCommDialog, setShowCommDialog,
    formData, setFormData,
    filteredAssociations,
    handleSave, handleDelete, cancelEdit,
    canEdit, linkedAssoc, assignedUser, linkedComms,
  } = useTaskDetail(task, { associations, users, communications, showAccountDropdown, onTaskUpdate, canEditAccount, onOpenChange, onSuccess });

  if (!task) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="task-detail-dialog">
        <DialogHeader>
          <DialogTitle>Task Details</DialogTitle>
        </DialogHeader>

        {isEditing ? (
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="edit-task-name">Task Name *</Label>
              <Input
                id="edit-task-name"
                value={formData.activity_name}
                onChange={(e) => setFormData({ ...formData, activity_name: e.target.value })}
                placeholder="e.g., Follow up on sale inquiry"
              />
            </div>

            {/* Conditional Row Layout based on context */}
            {showAccountDropdown ? (
              /* Horse Context: Show Account (left) + Association (right) */
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Linked Account (optional)</Label>
                  <Select 
                    value={formData.linked_account_id || 'none'} 
                    onValueChange={(val) => setFormData({ ...formData, linked_account_id: val === 'none' ? '' : val, association_id: '' })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select account" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      {accounts.map(acc => (
                        <SelectItem key={acc.id} value={acc.id}>
                          {acc.system_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label>Linked Association (optional)</Label>
                  <Select 
                    value={formData.association_id || 'none'} 
                    onValueChange={(val) => setFormData({ ...formData, association_id: val === 'none' ? '' : val })}
                    disabled={!formData.linked_account_id}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={formData.linked_account_id ? "Select association" : "Select account first"} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      {filteredAssociations.map(assoc => (
                        <SelectItem key={assoc.id} value={assoc.id}>
                          {assoc.entityName || assoc.relationship?.replace('_', ' ') || assoc.id}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            ) : (
              /* Account Context: Show Association + Horse */
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Linked Association</Label>
                  <Select 
                    value={formData.association_id || 'none'} 
                    onValueChange={(val) => setFormData({ ...formData, association_id: val === 'none' ? '' : val })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select association" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      {filteredAssociations.map(assoc => (
                        <SelectItem key={assoc.id} value={assoc.id}>
                          {assoc.entityName} - {assoc.relationship?.replace('_', ' ')}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Linked Horse</Label>
                  <Select 
                    value={formData.horse_id || 'none'} 
                    onValueChange={(val) => setFormData({ ...formData, horse_id: val === 'none' ? '' : val })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select horse" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      {horses.map(horse => (
                        <SelectItem key={horse.id} value={horse.id}>
                          {horse.system_name || horse.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit-task-due">Due Date</Label>
                <Input
                  id="edit-task-due"
                  type="date"
                  value={formData.end_date_time}
                  onChange={(e) => setFormData({ ...formData, end_date_time: e.target.value })}
                />
              </div>
              
              <div className="space-y-2">
                <Label>Status</Label>
                <Select 
                  value={formData.task_status} 
                  onValueChange={(val) => setFormData({ ...formData, task_status: val })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="not_started">Not Started</SelectItem>
                    <SelectItem value="in_progress">In Progress</SelectItem>
                    <SelectItem value="on_hold">On Hold</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                    <SelectItem value="cancelled">Cancelled</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Assigned To</Label>
              <Select 
                value={formData.assigned_to_user_id || 'none'} 
                onValueChange={(val) => setFormData({ ...formData, assigned_to_user_id: val === 'none' ? '' : val })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select user" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Unassigned</SelectItem>
                  {users.map(user => (
                    <SelectItem key={user.id} value={user.id}>
                      {user.name || `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.email}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Linked Communications (optional)</Label>
                <Button 
                  type="button" 
                  variant="outline" 
                  size="sm"
                  onClick={() => setShowCommDialog(true)}
                >
                  Create New
                </Button>
              </div>
              <div className="border rounded p-3 max-h-32 overflow-y-auto space-y-2">
                {communications.length === 0 ? (
                  <p className="text-xs text-muted-foreground">No communications available</p>
                ) : (
                  communications.slice(0, 10).map(comm => (
                    <div key={comm.id} className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        id={`edit-comm-${comm.id}`}
                        checked={(formData.linked_communication_ids || []).includes(comm.id)}
                        onChange={(e) => {
                          const currentIds = formData.linked_communication_ids || [];
                          if (e.target.checked) {
                            setFormData({ ...formData, linked_communication_ids: [...currentIds, comm.id] });
                          } else {
                            setFormData({ ...formData, linked_communication_ids: currentIds.filter(id => id !== comm.id) });
                          }
                        }}
                        className="rounded"
                      />
                      <Label htmlFor={`edit-comm-${comm.id}`} className="text-sm cursor-pointer flex-1">
                        {comm.activity_name} - {comm.communication_method?.replace('_', ' ')}
                      </Label>
                    </div>
                  ))
                )}
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="edit-task-notes">Notes</Label>
              <Textarea
                id="edit-task-notes"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Add details..."
                rows={3}
              />
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="edit-task-sensitive"
                checked={formData.is_sensitive}
                onChange={(e) => setFormData({ ...formData, is_sensitive: e.target.checked })}
                className="rounded"
              />
              <Label htmlFor="edit-task-sensitive" className="text-sm cursor-pointer">
                Mark as sensitive
              </Label>
            </div>
          </div>
        ) : (
          <div className="space-y-3 py-1">
            <div className="p-4 bg-muted/30 rounded">
              {task.is_sensitive && (
                <Badge variant="destructive" className="flex items-center gap-1 w-fit mb-2">
                  <AlertCircle className="w-3 h-3" />
                  Sensitive
                </Badge>
              )}
              
              <div className="flex items-start justify-between mb-3">
                <h3 className="text-lg font-semibold">{task.activity_name}</h3>
                <Badge variant={task.task_status === 'completed' ? 'default' : 'outline'} className="capitalize ml-3">
                  {task.task_status?.replace('_', ' ')}
                </Badge>
              </div>
              
              <div className="space-y-2 text-sm">
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-muted-foreground" />
                  <span className="text-muted-foreground">Due:</span>
                  <span>{task.end_date_time ? new Date(task.end_date_time).toLocaleDateString() : 'Not set'}</span>
                </div>
                
                <div className="flex items-center gap-2">
                  <User className="w-4 h-4 text-muted-foreground" />
                  <span className="text-muted-foreground">Assigned:</span>
                  <span>{assignedUser ? (assignedUser.name || `${assignedUser.first_name || ''} ${assignedUser.last_name || ''}`.trim() || assignedUser.email) : 'Unassigned'}</span>
                </div>
                
                {linkedAssoc && (
                  <div className="flex items-center gap-2">
                    <Link2 className="w-4 h-4 text-muted-foreground" />
                    <span className="text-muted-foreground">Linked Association:</span>
                    <span className="font-medium">{linkedAssoc.entityName}</span>
                    <Badge variant="secondary" className="text-xs">
                      {linkedAssoc.relationship?.replace('_', ' ')}
                    </Badge>
                  </div>
                )}
                
                {linkedComms && linkedComms.length > 0 && (
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <MessageSquare className="w-4 h-4 text-muted-foreground" />
                      <span className="text-muted-foreground">Linked Communications:</span>
                    </div>
                    <div className="ml-6 space-y-1">
                      {linkedComms.map(comm => (
                        <div key={comm.id} className="flex items-center gap-2">
                          <span 
                            className="text-sm text-primary hover:underline cursor-pointer"
                            onClick={() => {
                              if (onOpenCommDetail) {
                                onOpenCommDetail(comm);
                                onOpenChange(false);
                              }
                            }}
                          >
                            {comm.activity_name}
                          </span>
                          <Badge variant="secondary" className="text-xs">
                            {comm.communication_method?.replace('_', ' ')}
                          </Badge>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Linked Notes — horse page only, via conformation_note_id */}
                {(() => {
                  const linkedNote = task?.conformation_note_id
                    ? horseNotes.find(n => n.id === task.conformation_note_id)
                    : null;
                  if (!linkedNote) return null;
                  const NOTE_TYPE_BADGE = { conformation_note: 'Conformation', vet_procedure_note: 'Vet Procedure', layup_note: 'Layup', sales_inspection_note: 'Sale Inspection', other_note: 'Other' };
                  const nt = linkedNote.note_type || 'conformation_note';
                  return (
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <FileText className="w-4 h-4 text-muted-foreground" />
                        <span className="text-muted-foreground">Linked Note:</span>
                      </div>
                      <div className="ml-6 flex items-center gap-2 cursor-pointer"
                        onClick={() => { if (onOpenNoteDetail) { onOpenNoteDetail(linkedNote); onOpenChange(false); } }}>
                        <span className="text-sm text-primary hover:underline">{linkedNote.title}</span>
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground">
                          {NOTE_TYPE_BADGE[nt] || nt}
                        </span>
                      </div>
                    </div>
                  );
                })()}
              </div>
            </div>

            {task.description && (
              <div className="space-y-1 pt-2">
                <Label className="text-sm font-medium">Notes</Label>
                <p className="text-sm whitespace-pre-wrap text-muted-foreground">{task.description}</p>
              </div>
            )}

            <div className="text-xs text-muted-foreground pt-2 border-t">
              Created: {new Date(task.created_at).toLocaleDateString()} by {usersMap[task.created_by_id] || 'Unknown'}
            </div>
          </div>
        )}

        <ActivityDialogFooter
          isEditing={isEditing}
          saving={saving}
          onSave={handleSave}
          onCancel={cancelEdit}
          onDelete={handleDelete}
          onClose={() => onOpenChange(false)}
          canEdit={canEdit}
          onEdit={() => setIsEditing(true)}
          deleteTestId="delete-task-btn"
          editTestId="edit-task-btn"
        />
      </DialogContent>
      
      {/* Nested Communication Dialog */}
      {showCommDialog && (
        <CommunicationDialog
          open={showCommDialog}
          onOpenChange={setShowCommDialog}
          accountId={task.crm_entity_id}
          associations={associations}
          users={users}
          contactEntries={contactEntries}
          onSuccess={() => {
            setShowCommDialog(false);
            onSuccess?.(); // Refresh parent data
          }}
        />
      )}
    </Dialog>
  );
};

